import IUserUI from "@models/IUserUI";
import ISupplierModel from "@models/ISupplierModel";
import AccessTypesEnum from "@models/AccessTypesEnum";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";
import UserTypesEnum from "@models/UserTypesEnum";
import { hasReadAccess } from "@services/ScreenOptionsService";
import UserService from "@services/UserService";
import ViewAccessService from "@services/ViewAccessService";

/**
 * Admins, Sales users with full access and Inventory Mgmt: the roles the API serves the log to
 * (InventoryService.canViewInventoryLogs). The log lists every order and PO that touched the item, across all
 * accounts, so it is narrower than the Inventory screen.
 */
export function hasInventoryLogRole(userModel: IUserUI): boolean {
    const token = userModel.supplierUserToken;
    const userType = userModel.userType?.id ?? token?.user_type_id;

    switch (userType) {
        case UserTypesEnum.ADMIN:
        case UserTypesEnum.INVENTORY_MGMT:
            return true;
        case UserTypesEnum.SALES:
            return (token?.access_type_id ?? token?.Access_Type?.id) === AccessTypesEnum.FULL;
        default:
            return false;
    }
}

/**
 * Whether the user can open an item's inventory log (OR-1555): supplier users in one of the log's roles who can also
 * read the Inventory screen and are not kept from inventory details.
 */
export function canViewInventoryLog(userModel: IUserUI, supplierModel: ISupplierModel): boolean {
    return (
        !!userModel.isSupplierUser &&
        hasInventoryLogRole(userModel) &&
        UserService.showInventoryDetails(supplierModel, userModel) &&
        hasReadAccess(ScreenOptionsEnum.INVENTORY, userModel, supplierModel, ViewAccessService.Inventory)
    );
}

/** The inventory log page for one item, optionally narrowed to one order or PO (the order and PO line links). */
export function inventoryLogPath(catalogItemGuid: string, scope: { orderGuid?: string; poGuid?: string } = {}): string {
    const params = new URLSearchParams();

    if (scope.orderGuid) {
        params.set("order_guid", scope.orderGuid);
    }

    if (scope.poGuid) {
        params.set("po_guid", scope.poGuid);
    }

    const search = params.toString();

    return `/inventory/items/${encodeURIComponent(catalogItemGuid)}${search ? `?${search}` : ""}`;
}
